import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationErrorStatus, AuthenticationRoleResponse, GeneralSetting, PortalEntity, PortalEntityResponse, PortalLoginResponse, UserType } from '@jms/model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AUTH_TOKEN_KEY } from '@jms/utils/util.authentication';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';;
import { UtilService } from '@jms/utils/util.services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, tap, withLatestFrom } from 'rxjs/operators';
import { authenticateLoginUserErrorAction, getPortalEntitiesFailAction, getPortalEntitiesSuccessAction, handleInvalidUserAction, handleValidUserAction, logoutAction, } from './application.actions';
import { Store } from '@ngrx/store';
import { ApplicationActionName } from './application.model';
import { ContextService } from '../services/context.service';
import { ToastService } from '@workbuddy/ui/services';
import * as Enumerable from 'linq';

const environment = ConfigService.environment;
@Injectable()
export class ApplicationEffects {

    private authPath = './api/authentication/';
    private maintenanceAnnouncedForToken: string | null = null;

    constructor(
        private http: HttpClient,
        private actions$: Actions,
        private context: ContextService,
        private router: Router,
        private store: Store<any>,
        private toasts: ToastService
    ) { }


    public authenticateLoginUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.AuthenticateLoginUser),
            concatMap((x: any) => {

                const request = x.request;

                return this.http
                    .post<AuthenticationRoleResponse>(this.authPath + 'role', request)
                    .pipe(
                        map(login => {

                            if (!login.success) {
                                switch (login.errorStatus) {
                                    case AuthenticationErrorStatus.UserNotActive: {
                                        return handleInvalidUserAction({ url: '/inactive/user' });
                                    }

                                    case AuthenticationErrorStatus.UserDeleted: {
                                        return handleInvalidUserAction({ url: '/deleted/user' });
                                    }

                                    case AuthenticationErrorStatus.UserInactiveStatus: {
                                        return handleInvalidUserAction({ url: '/inactive/status/user' });
                                    }

                                    case AuthenticationErrorStatus.UserCannotLoginViaWeb: {
                                        return handleInvalidUserAction({ url: '/login/access-denied' });
                                    }

                                    case AuthenticationErrorStatus.TokenWasUsed: {
                                        return handleInvalidUserAction({ url: '/home' });
                                    }

                                    case AuthenticationErrorStatus.LoginsDisabled: {
                                        return handleInvalidUserAction({ url: '/login/maintenance' });
                                    }

                                }
                            }

                            const loginSuccess = login?.success && !!login?.token;

                            const loginSuccessData = { token: login.token, roleToken: login.roleToken, returnUrl: x.request.returnUrl, maintenanceTenants: login.maintenanceTenants } as PortalLoginResponse;

                            return loginSuccess
                                ? handleValidUserAction({ response: loginSuccessData })
                                : logoutAction();

                        }),
                        catchError(error => {
                            console.error(error);
                            return of(authenticateLoginUserErrorAction(error));
                        })
                    );
            })
        ));


    getPortalEntities$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.HandleValidUser, ApplicationActionName.GetPortalEntities),
            withLatestFrom(
                this.store.select(state => state.application.user),
            ),
            concatMap(([action, user]) => {

                const request = {
                    userId: user._id,
                    tenants: user.tenants
                };

                return this.http
                    .post<PortalEntityResponse[]>(this.authPath + 'entities', request)
                    .pipe(
                        map(response => {

                            const safeResponse = Enumerable
                                .from(response)
                                .where(x => x?.entities?.length > 0)
                                .toArray()

                            if (!safeResponse?.length)
                                return handleInvalidUserAction({ url: '/login/access-denied' });

                            return getPortalEntitiesSuccessAction({ response: safeResponse });

                        }),
                        catchError(error => of(getPortalEntitiesFailAction(error))
                        )
                    );
            })
        ));



    getPortalEntitiesSuccess$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.GetPortalEntitiesSuccess),
            withLatestFrom(
                this.store.select(state => state.application.returnUrl),
                this.store.select(state => state.application.maintenanceTenants),
                this.store.select(state => state.application.token),
            ),
            tap(async ([action, returnUrl, maintenanceTenants, token]) => {

                if (returnUrl)
                    await this.router.navigate([returnUrl]);

                // Raised after navigation because the toast host lives in the portal shell, which only renders once routed.
                // Entities can load more than once per sign-in (login and the auth guard), so announce once per token.
                if (maintenanceTenants?.length && this.maintenanceAnnouncedForToken !== token) {
                    this.maintenanceAnnouncedForToken = token;
                    this.toasts.warning('Under maintenance', `${maintenanceTenants.join(', ')} is currently under maintenance and unavailable. Please try again later.`, undefined, { sticky: true });
                }
            })
        ), { dispatch: false });

    // @Effect({ dispatch: false })
    // getTenantGeneralSetting$ = this.actions$
    //     .pipe(
    //         ofType(ApplicationActionName.ChangePortalEntity),
    //         withLatestFrom(
    //             this.store.select(state => state.application.selectedEntity),
    //             this.store.select(state => state.application.info)
    //         ),
    //         concatMap(([action, selectedEntity, info]: [any, PortalEntity, any]) => {

    //             const { entity } = action;

    //             if (entity?.tenantName === selectedEntity?.tenantName)
    //                 return of(null);


    //             return this.http
    //                 .get<GeneralSetting>(`${this.settingAPI}`)
    //                 .pipe(
    //                     map(response => {
    //                         return getTenantSettingsActionSuccess({ setting: response });
    //                     }),
    //                     catchError(error => of(getTenantSettingsActionFail(error)))
    //                 );
    //         })
    //     );




    routerNavigating$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.HandleInvalidUser),
            map((x: any) => {
                this.router.navigate([x.url]);
            })
        ), { dispatch: false });

    // routerReturnNavigating$ = createEffect(() => this.actions$
    //     .pipe(
    //         ofType(ApplicationActionName.HandleValidUser),
    //         map((x: any) => {
    //             this.router.navigate([x.response.returnUrl]);
    //         })
    //     ), { dispatch: false });


    logout$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.LogOut),
            map(x => {
                // In path mode the tenant origin owns the ID handoff and the env.sso hint
                // (the portal's own config.js has neither) — clear both session copies and
                // let the tenant AuthGuard run the ID logout redirect with the right SSO config.
                if (this.context.pathPrefix) {
                    this.clearSharedOriginContactToken();
                    window.location.assign(this.context.origin);
                    return;
                }

                UtilService.navigate(environment.jms.logoutHref, `${this.context.origin}/callback`);
            })
        ), { dispatch: false });

    // In path mode the tenant guard re-seeds portal_auth_token from the shared-origin
    // auth_token, so a contact logout must drop that copy too or the next visit logs
    // them straight back in. An employee tenant session from another tab is left alone.
    private clearSharedOriginContactToken(): void {

        if (!this.context.pathPrefix) return;

        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) return;

        try {
            const user = new JwtHelperService({}).decodeToken(token);
            const isContact = user?.role?.type === UserType.LoginContact || Enumerable.from((user?.tenants ?? []) as any[]).any(t => t?.role?.type === UserType.LoginContact);

            if (isContact) {
                localStorage.removeItem(AUTH_TOKEN_KEY);
            }
        } catch {
            // Unreadable token — leave it for the tenant app to deal with.
        }
    }


}
